'use client';

import { useEffect, useState, Suspense, useMemo } from 'react';
import { APIProvider, Map, Marker, InfoWindow } from '@vis.gl/react-google-maps';
import { getPropertiesNearCoordinates } from '@/app/actions/Properties/actions';
import { useSearchParams } from 'next/navigation';
import PropertyInfoWindow, {
  type PropertyInfoWindowProperty,
} from '@/components/elements/Properties/PropertyInfoWindow';
import { Logo } from '@/components/elements/Layout/Navbar/Logo';
import { formatCurrencyWithCode } from '@/lib/utils';
import useCurrency from '@/hooks/useCurrency';
import { Card, CardContent, CardTitle, CardHeader } from '@/components/ui/card';
import { Button } from '@/components/ui/button';

const API_KEY = process.env.NEXT_PUBLIC_GOOGLE_MAP_API_KEY as string;

type Property = PropertyInfoWindowProperty;

// Function to create a custom price marker icon
function createPriceMarkerIcon(formattedPrice: string): string {
  // Calculate width based on price length
  const priceLength = formattedPrice.length;
  const baseWidth = 60;
  const charWidth = 7;
  const padding = 12;
  const width = Math.max(baseWidth, priceLength * charWidth + padding);
  const height = 22; // Thin badge
  const borderRadius = 12;

  // Create simple white badge with black text - no pin point
  const svg = `<svg width="${width}" height="${height}" xmlns="http://www.w3.org/2000/svg">
  
  <rect x="0.5" y="0.5" width="${width - 1}" height="${height - 1}" rx="${borderRadius}" fill="#000" stroke="#000" stroke-width="1"/>
  <!-- Price text - black -->
  <text x="${width / 2}" y="${height / 2 + 3}" font-family="Arial, Helvetica, sans-serif" font-size="12" font-weight="bold" fill="#FFEE71" text-anchor="middle" dominant-baseline="middle">${formattedPrice}</text>
</svg>`;

  // Properly encode SVG for data URL
  const encodedSvg = encodeURIComponent(svg);
  return `data:image/svg+xml;charset=utf-8,${encodedSvg}`;
}

function WidgetContent() {
  const searchParams = useSearchParams();
  const [properties, setProperties] = useState<Property[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedProperty, setSelectedProperty] = useState<Property | null>(null);
  const [error, setError] = useState<string | null>(null);
  const { currency } = useCurrency();

  // Function to get marker icon for a property
  const getMarkerIcon = useMemo(() => {
    return (property: Property) => {
      // Format price using currency component logic
      const formattedPrice = formatCurrencyWithCode(property.basePrice, currency);
      const iconUrl = createPriceMarkerIcon(formattedPrice);

      if (typeof window === 'undefined' || !(window as Window).google?.maps) {
        return { url: iconUrl };
      }

      const google = (window as Window).google.maps;
      // Calculate dynamic size based on formatted price length
      const priceLength = formattedPrice.length;
      const baseWidth = 40;
      const charWidth = 7;
      const padding = 12;
      const width = Math.max(baseWidth, priceLength * charWidth + padding);
      const height = 22; // Thin badge, no pin

      return {
        url: iconUrl,
        scaledSize: new google.Size(width, height),
        anchor: new google.Point(width / 2, height / 2), // Center anchor
      };
    };
  }, [currency]);

  // Get parameters from URL
  const latParam = searchParams.get('lat');
  const lngParam = searchParams.get('lng');
  const referral = searchParams.get('referral') || '';

  const lat = latParam ? parseFloat(latParam) : null;
  const lng = lngParam ? parseFloat(lngParam) : null;

  useEffect(() => {
    if (!lat || !lng || isNaN(lat) || isNaN(lng)) {
      setError('Invalid coordinates. Please provide valid lat and lng parameters.');
      setLoading(false);
      return;
    }

    const fetchProperties = async () => {
      try {
        setLoading(true);
        const properties = await getPropertiesNearCoordinates(lat, lng, 50); // 50km radius
        setProperties(properties);
      } catch (err) {
        setError('An error occurred while fetching properties');
        console.error(err);
        console.error(
          `To use the widget, please embed a URL with a simillar structure to your iframe: <iframe src="https://kawkb.com/widget?lat=30.0444&lng=31.2357&referral=name" width="100%" height="100%"></iframe>`
        );
      } finally {
        setLoading(false);
      }
    };

    fetchProperties();
  }, [lat, lng]);

  // Default center (Cairo, Egypt) if no coordinates provided
  const centerLat = lat || 30.0444;
  const centerLng = lng || 31.2357;

  if (error) {
    return (
      <Card className="max-w-lg mx-auto mt-8">
        <CardHeader>
          <CardTitle>Oh no🙈! </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-base">
            We apologize for the inconvenience. Our team has been notified and is working to fix the
            issue.
          </p>
          <div className="flex gap-2">
            <Button onClick={() => window.location.reload()}>Refresh Page</Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="w-full h-screen relative">
      {loading ? (
        <LoadingNearByProperties />
      ) : (
        <APIProvider apiKey={API_KEY}>
          <Logo size="md" color="yellow" className="absolute top-4 left-4 z-10 bg-primary p-3" />
          <Map
            className="w-full h-full"
            defaultZoom={12}
            defaultCenter={{
              lat: centerLat,
              lng: centerLng,
            }}
            gestureHandling={'greedy'}
            disableDefaultUI={false}
            mapTypeControl={false}
            fullscreenControl={false}
          >
            {properties.map((property) => (
              <Marker
                key={property.id}
                position={{
                  lat: property.pin.lat,
                  lng: property.pin.lng,
                }}
                onClick={() => setSelectedProperty(property)}
                title={property.title}
                icon={getMarkerIcon(property)}
              />
            ))}

            {selectedProperty && (
              <InfoWindow
                position={{
                  lat: selectedProperty.pin.lat,
                  lng: selectedProperty.pin.lng,
                }}
                onCloseClick={() => setSelectedProperty(null)}
              >
                <PropertyInfoWindow property={selectedProperty} referral={referral} />
              </InfoWindow>
            )}
          </Map>
        </APIProvider>
      )}
    </div>
  );
}

function LoadingNearByProperties() {
  return (
    <div className="w-full h-screen flex items-center justify-center bg-white flex flex-col gap-4">
      <Logo size="md" color="yellow" className="bg-primary p-3 animate-spin-pause" />
    </div>
  );
}

export default function WidgetPage() {
  return (
    <Suspense fallback={<LoadingNearByProperties />}>
      <WidgetContent />
    </Suspense>
  );
}
